import React, { useEffect } from 'react';
import { useStore } from '../../../../../stores/root';

// assets
import { faCheck, faClose } from '@fortawesome/pro-light-svg-icons';

// services
import ValidationService from '../../../../../services/validation';
import VendorsService from '../../../../../services/data/vendors';

// props
import { IRoleResourceShortProps } from '../../../../../props/users/role';

// components
import { ActionButton, DatePicker, DefaultButton, Panel, PanelType, Pivot, PivotItem, PrimaryButton, Spinner, SpinnerSize, Stack, TextField } from '@fluentui/react';
import moment from 'moment';
import PermissionsService from '../../../../../services/permissions';
import ErrorService from '../../../../../services/general/error';
import NoAccess from '../../../../uiframeworks/noAccess';
import Text from '../../../../typography/text';
import { IVendorResourceProps } from '../../../../../props/data/vendors';
import VendorDocumentsForm from './documents';
import VendorDetailsForm from './details';
import VendorPICForm from './pic';
import Label from '../../../../typography/label';
import { IDocumentProps, IFileProps } from '../../../../../props/general';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faXmarkCircle } from '@fortawesome/pro-solid-svg-icons';
import { IconProp } from '@fortawesome/fontawesome-svg-core';

type VerifyVendorFormProps = {
    vendorId: string;
    defaultStatus?: 'verified' | 'verified_with_risk';
    isExtension?: boolean;
    onDismissed(refresh?: boolean, vendor?: IVendorResourceProps): void;
}

const VerifyVendorForm: React.FC<VerifyVendorFormProps> = (props: VerifyVendorFormProps) => {
    const { banner, user } = useStore();
    const [loaded, setLoaded] = React.useState<boolean>(false);
    const [justification, setJustification] = React.useState<string>("");
    const [approvedFrom, setApprovedFrom] = React.useState<Date | undefined>(new Date());
    const [approvedUntil, setApprovedUntil] = React.useState<Date | undefined>(
        moment().add(3, 'months').toDate()
    );
    const [error, setError] = React.useState<any>({});
    const [submitting, setSubmitting] = React.useState<boolean>(false);
    const [vendor, setVendor] = React.useState<IVendorResourceProps | undefined>();

    const hasVerifyPermission = PermissionsService.hasPermission(['vendors.verification.verify'], user.permissions);
    const hasVerifyWithRiskPermission = PermissionsService.hasPermission(['vendors.verification.verify_with_risk'], user.permissions);
    const hasPermission = hasVerifyPermission || hasVerifyWithRiskPermission;

    const isRenewal = vendor?.verificationStatus === 'expired';
    const isExtensionOrRenewal = props.isExtension || isRenewal;

    useEffect(() => {
        init();
    }, []);

    const init = async () => {
        const _vendor = await VendorsService.get(props.vendorId);
        setVendor(_vendor);

        if (_vendor?.verificationStatus === 'expired') {
            // Renewal contract mode
            setApprovedFrom(new Date());
            setApprovedUntil(moment().add(3, 'months').toDate());
        } else if (props.isExtension && _vendor?.approvedUntil) {
            const currentExpiry = moment(_vendor.approvedUntil);
            const baseFrom = currentExpiry.isAfter(moment()) ? currentExpiry : moment();
            setApprovedFrom(baseFrom.toDate());
            setApprovedUntil(baseFrom.clone().add(3, 'months').toDate());
        } else if (_vendor?.approvedFrom && _vendor?.approvedUntil) {
            setApprovedFrom(moment(_vendor.approvedFrom).toDate());
            setApprovedUntil(moment(_vendor.approvedUntil).toDate());
        } else {
            setApprovedFrom(new Date());
            setApprovedUntil(moment().add(3, 'months').toDate());
        }
        setLoaded(true);
    }

    const _onSubmit = async (status: 'verified' | 'verified_with_risk') => {
        try {
            if (!approvedFrom) {
                banner.add({
                    key: 'approved_from_required',
                    variant: 'error',
                    icon: faClose,
                    text: 'Silakan pilih tanggal mulai berlaku approval (Approved From).'
                });
                return;
            }

            if (!approvedUntil) {
                banner.add({
                    key: 'approved_until_required',
                    variant: 'error',
                    icon: faClose,
                    text: 'Silakan pilih tanggal masa berlaku approval (Approved Until).'
                });
                return;
            }

            if (moment(approvedUntil).isBefore(moment(approvedFrom), 'day')) {
                banner.add({
                    key: 'approved_until_invalid',
                    variant: 'error',
                    icon: faClose,
                    text: 'Tanggal Approved Until tidak boleh lebih awal dari tanggal Approved From.'
                });
                return;
            }

            if (moment(approvedUntil).isBefore(moment(), 'day')) {
                banner.add({
                    key: 'approved_until_past',
                    variant: 'error',
                    icon: faClose,
                    text: 'Tanggal Approved Until tidak boleh berada di masa lalu.'
                });
                return;
            }

            const confirmMsg = isRenewal
                ? `Apakah Anda yakin ingin memperbarui kontrak (renewal) vendor "${vendor?.name}" dari ${moment(approvedFrom).format('DD/MM/YYYY')} s/d ${moment(approvedUntil).format('DD/MM/YYYY')}?`
                : (props.isExtension
                    ? `Are you sure you want to extend the approval for "${vendor?.name}" until ${moment(approvedUntil).format('DD/MM/YYYY')}?`
                    : (status === 'verified_with_risk'
                        ? "Are you sure to set this vendor as verified with risk?"
                        : "Are you sure to set this vendor as verified?"));

            if (window.confirm(confirmMsg)) {
                setSubmitting(true);

                const fd = new FormData();
                fd.append('justification', justification || "");
                fd.append('verificationStatus', status);
                fd.append('approvedFrom', moment(approvedFrom).format('YYYY-MM-DD'));
                fd.append('approvedUntil', moment(approvedUntil).format('YYYY-MM-DD'));
                if (props.isExtension || isRenewal) {
                    fd.append('isExtension', 'true');
                }
                await VendorsService.verify(props.vendorId, fd);

                banner.add({
                    key: 'verifiy_vendor_success',
                    variant: 'success',
                    icon: faCheck,
                    text: isRenewal
                        ? `Kontrak kerja sama vendor "${vendor?.name}" berhasil diperbarui (Renewal) hingga ${moment(approvedUntil).format('DD/MM/YYYY')}`
                        : (props.isExtension
                            ? `Approval for vendor "${vendor?.name}" extended until ${moment(approvedUntil).format('DD/MM/YYYY')}`
                            : `Vendor "${vendor?.name}" is now ${status === 'verified_with_risk' ? 'verified with risk' : 'verified'} until ${moment(approvedUntil).format('DD/MM/YYYY')}`)
                });
                props.onDismissed(true);
            }
        } catch (e) {
            banner.add({
                key: 'verify_vendor_error',
                variant: 'error',
                icon: faClose,
                text: ErrorService.getMessage(e)
            });
            setSubmitting(false);
        }
    }

    const renderRelatedDocuments = (documents: IDocumentProps[]) => {
        const npwpDocument = documents.find((d) => (d.type || "").toLowerCase() === 'npwp');
        const nibDocument = documents.find((d) => (d.type || "").toLowerCase() === 'nib');
        const ownerIdDocument = documents.find((d) => (d.type || "").toLowerCase() === 'owner id');

        return <Stack tokens={{ childrenGap: 10 }}>
            <Label size={"small"}>Documents</Label>
            <Stack horizontal tokens={{ childrenGap: 5 }} verticalAlign={'center'}>
                <FontAwesomeIcon fontSize={14} icon={(npwpDocument ? faCheckCircle : faXmarkCircle) as IconProp} style={{ marginTop: -3 }} className={npwpDocument ? 'color-green' : 'color-red'} />
                <Text size={'small'} className={npwpDocument ? 'color-green' : 'color-red'}>{npwpDocument ? "NPWP document found" : "No NPWP document found"}</Text>
            </Stack>
            <Stack horizontal tokens={{ childrenGap: 5 }} verticalAlign={'center'}>
                <FontAwesomeIcon fontSize={14} icon={(nibDocument ? faCheckCircle : faXmarkCircle) as IconProp} style={{ marginTop: -3 }} className={nibDocument ? 'color-green' : 'color-red'} />
                <Text size={'small'} className={nibDocument ? 'color-green' : 'color-red'}>{nibDocument ? "NIB document found" : "No NIB document found"}</Text>
            </Stack>
            <Stack horizontal tokens={{ childrenGap: 5 }} verticalAlign={'center'}>
                <FontAwesomeIcon fontSize={14} icon={(ownerIdDocument ? faCheckCircle : faXmarkCircle) as IconProp} style={{ marginTop: -3 }} className={ownerIdDocument ? 'color-green' : 'color-red'} />
                <Text size={'small'} className={ownerIdDocument ? 'color-green' : 'color-red'}>{ownerIdDocument ? "Owner ID (KTP) document found" : "No Owner ID (KTP) document found"}</Text>
            </Stack>
        </Stack>
    }

    const getHeaderText = () => {
        if (isRenewal) {
            return `Renewal Contract: ${vendor?.name || ''}`;
        }
        if (props.isExtension) {
            return `Extend Approval: ${vendor?.name || ''}`;
        }
        return props.defaultStatus === 'verified_with_risk' ? "Set as Verified with Risk" : "Set as Verified Vendor";
    };

    return <Panel headerText={getHeaderText()}
        isOpen={true}
        type={PanelType.medium}
        onDismiss={() => props.onDismissed(false)}
        isFooterAtBottom={true}
        onRenderFooterContent={() => {
            return <Stack horizontal tokens={{ childrenGap: 10 }}>
                {
                    !submitting && hasPermission ? (
                        <>
                            {isExtensionOrRenewal ? (
                                <>
                                    {hasVerifyPermission && (
                                        <PrimaryButton
                                            text={isRenewal ? "Renewal Contract" : "Extend Approval"}
                                            onClick={() => _onSubmit('verified')}
                                        />
                                    )}
                                    {hasVerifyWithRiskPermission && (
                                        <DefaultButton
                                            text={isRenewal ? "Renewal with Risk" : "Extend with Risk"}
                                            onClick={() => _onSubmit('verified_with_risk')}
                                            styles={{
                                                root: {
                                                    backgroundColor: '#fff',
                                                    color: '#d83b01',
                                                    borderColor: '#d83b01',
                                                },
                                                rootHovered: {
                                                    backgroundColor: '#fde7e9',
                                                    color: '#d83b01',
                                                    borderColor: '#d83b01',
                                                },
                                                rootPressed: {
                                                    backgroundColor: '#fde7e9',
                                                    color: '#d83b01',
                                                    borderColor: '#d83b01',
                                                }
                                            }}
                                        />
                                    )}
                                    <DefaultButton text={"Cancel"} onClick={() => { props.onDismissed(false) }} />
                                </>
                            ) : props.defaultStatus === 'verified_with_risk' ? (
                                <>
                                    {hasVerifyWithRiskPermission && (
                                        <PrimaryButton
                                            text={"Set as Verified with Risk"}
                                            onClick={() => _onSubmit('verified_with_risk')}
                                            styles={{
                                                root: {
                                                    backgroundColor: '#d83b01',
                                                    color: '#fff',
                                                    borderColor: '#d83b01',
                                                },
                                                rootHovered: {
                                                    backgroundColor: '#b83201',
                                                    color: '#fff',
                                                    borderColor: '#b83201',
                                                },
                                                rootPressed: {
                                                    backgroundColor: '#a32c01',
                                                    color: '#fff',
                                                    borderColor: '#a32c01',
                                                }
                                            }}
                                        />
                                    )}
                                    {hasVerifyPermission && (
                                        <DefaultButton text={"Set as Verified"} onClick={() => _onSubmit('verified')} />
                                    )}
                                    <DefaultButton text={"Cancel"} onClick={() => { props.onDismissed(false) }} />
                                </>
                            ) : (
                                <>
                                    {hasVerifyPermission && (
                                        <PrimaryButton text={"Set as Verified"} onClick={() => _onSubmit('verified')} />
                                    )}
                                    {hasVerifyWithRiskPermission && (
                                        <DefaultButton
                                            text={"Set as Verified with Risk"}
                                            onClick={() => _onSubmit('verified_with_risk')}
                                            styles={{
                                                root: {
                                                    backgroundColor: '#fff',
                                                    color: '#d83b01',
                                                    borderColor: '#d83b01',
                                                },
                                                rootHovered: {
                                                    backgroundColor: '#fde7e9',
                                                    color: '#d83b01',
                                                    borderColor: '#d83b01',
                                                },
                                                rootPressed: {
                                                    backgroundColor: '#fde7e9',
                                                    color: '#d83b01',
                                                    borderColor: '#d83b01',
                                                }
                                            }}
                                        />
                                    )}
                                    <DefaultButton text={"Cancel"} onClick={() => { props.onDismissed(false) }} />
                                </>
                            )}
                        </>
                    ) : null
                }
                {submitting ? <Spinner size={SpinnerSize.medium} labelPosition={"right"} label={"Menyimpan data ..."} /> : null}
            </Stack>;
        }}>
        <Stack>
            {!loaded ? <Stack horizontalAlign={"baseline"} styles={{ root: { marginTop: 20 } }}><Spinner size={SpinnerSize.medium} labelPosition={"right"} label={"Preparing form ..."} /></Stack> : null}
            {
                loaded && vendor && hasPermission ? <>
                    <Stack tokens={{ childrenGap: 20 }}>
                        <Text>
                            {isRenewal
                                ? `Masa berlaku kontrak kerja sama vendor ini telah berakhir pada ${vendor.approvedUntil ? moment(vendor.approvedUntil).format('DD/MM/YYYY') : '-'}. Silakan tentukan periode kontrak baru dan catatan justifikasi/nomor PKS untuk mengaktifkan kembali vendor ini.`
                                : (props.isExtension
                                    ? `Perpanjang masa berlaku approval vendor dengan detail di bawah ini.`
                                    : (props.defaultStatus === 'verified_with_risk'
                                        ? "Are you sure you want to set vendor with details below as verified with risk?"
                                        : "Are you sure you want to set vendor with details below as verified?"))}
                        </Text>
                        <Stack>
                            <Label size={'xsmall'}>Name</Label>
                            <Text size={'small'}>{vendor.name}</Text>
                        </Stack>
                        <Stack>
                            <Label size={'xsmall'}>Address</Label>
                            <Text size={'small'}>{vendor.address || "-"}</Text>
                        </Stack>
                        <Stack>
                            <Label size={'xsmall'}>Phone number</Label>
                            <Text size={'small'}>{vendor.phoneNumber || "-"}</Text>
                        </Stack>
                        <Stack>
                            <Label size={'xsmall'}>NPWP</Label>
                            <Text size={'small'}>{vendor.npwp || "-"}</Text>
                        </Stack>
                        <Stack className={'divider'} />
                        {renderRelatedDocuments(vendor.documents || [])}
                        <Stack className={'divider'} />
                        <Stack>
                            <Label size={'xsmall'}>Owner name</Label>
                            <Text size={'small'}>{vendor.ownerName || "-"}</Text>
                        </Stack>
                        <Stack>
                            <Label size={'xsmall'}>Onwer ID number</Label>
                            <Text size={'small'}>{vendor.ownerIdentification || "-"}</Text>
                        </Stack>
                        <Stack className={'divider'}></Stack>
                        
                        {/* Dua Tanggal: Approved From & Approved Until */}
                        <Stack tokens={{ childrenGap: 10 }}>
                            <Stack horizontal tokens={{ childrenGap: 15 }}>
                                <Stack styles={{ root: { width: '50%' } }}>
                                    <DatePicker
                                        label={'Approved From (Effective Date)'}
                                        isRequired={true}
                                        value={approvedFrom}
                                        onSelectDate={(date) => {
                                            setApprovedFrom(date || undefined);
                                            if (date && approvedUntil && moment(approvedUntil).isBefore(moment(date))) {
                                                setApprovedUntil(moment(date).add(3, 'months').toDate());
                                            }
                                        }}
                                        formatDate={(date) => date ? moment(date).format('DD/MM/YYYY') : ''}
                                    />
                                </Stack>
                                <Stack styles={{ root: { width: '50%' } }}>
                                    <DatePicker
                                        label={'Approved Until (Expiry Date)'}
                                        isRequired={true}
                                        value={approvedUntil}
                                        minDate={approvedFrom || new Date()}
                                        onSelectDate={(date) => setApprovedUntil(date || undefined)}
                                        formatDate={(date) => date ? moment(date).format('DD/MM/YYYY') : ''}
                                    />
                                </Stack>
                            </Stack>

                            <Stack horizontal tokens={{ childrenGap: 8 }} wrap verticalAlign={'center'}>
                                <Text size={'xsmall'} style={{ color: '#666' }}>Quick Options (from start date):</Text>
                                <ActionButton
                                    styles={{ root: { height: 26, padding: '0 8px', fontSize: 12 } }}
                                    onClick={() => setApprovedUntil(moment(approvedFrom || new Date()).add(1, 'month').toDate())}
                                >
                                    +1 Month
                                </ActionButton>
                                <ActionButton
                                    styles={{ root: { height: 26, padding: '0 8px', fontSize: 12 } }}
                                    onClick={() => setApprovedUntil(moment(approvedFrom || new Date()).add(3, 'months').toDate())}
                                >
                                    +3 Months (Default)
                                </ActionButton>
                                <ActionButton
                                    styles={{ root: { height: 26, padding: '0 8px', fontSize: 12 } }}
                                    onClick={() => setApprovedUntil(moment(approvedFrom || new Date()).add(6, 'months').toDate())}
                                >
                                    +6 Months
                                </ActionButton>
                                <ActionButton
                                    styles={{ root: { height: 26, padding: '0 8px', fontSize: 12 } }}
                                    onClick={() => setApprovedUntil(moment(approvedFrom || new Date()).add(1, 'year').toDate())}
                                >
                                    +1 Year
                                </ActionButton>
                            </Stack>

                            {approvedFrom && approvedUntil && (
                                <Text size={'xsmall'} style={{ color: '#0078d4' }}>
                                    Active contract period: <strong>{moment(approvedFrom).format('DD MMMM YYYY')}</strong> to <strong>{moment(approvedUntil).format('DD MMMM YYYY')}</strong> ({moment(approvedUntil).diff(moment(approvedFrom), 'days')} days)
                                </Text>
                            )}
                        </Stack>

                        <TextField 
                            label={isRenewal ? 'Renewal Notes / New PKS Number' : (props.isExtension ? 'Extension Justification / Notes' : 'Justification')}
                            value={justification}
                            multiline
                            autoAdjustHeight
                            resizable={false}
                            required={true}
                            errorMessage={error.justification}
                            onChange={(evt, value) => {
                                const validation = ValidationService.combination(value, ['required', 'limit'], { maxChars: 1000 });
                                error.justification = validation.message;

                                setJustification(value || "");
                                setError({ ...error });
                            }} 
                        />
                    </Stack>
                </> : null
            }
            {!hasPermission ? <NoAccess /> : null}
        </Stack>
    </Panel>
};

export default VerifyVendorForm;
